import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { TopProspect } from '@/services/topProspectsService';
import { cn } from '@/lib/utils';
import { topProspectsService } from '@/services/topProspectsService';

interface TopProspectsTableProps {
  prospects: TopProspect[];
  onRefresh?: () => void;
  refreshing?: boolean;
  loading?: boolean;
  className?: string;
}

const TopProspectsTable = ({
  prospects,
  onRefresh,
  refreshing,
  loading,
  className
}: TopProspectsTableProps) => {
  const [sortBy, setSortBy] = useState<'name' | 'stage' | 'date'>('date');

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      'New': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'Contacted': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'Connected': 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300',
      'Qualified': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Negotiation': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'Proposal': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'Trial Received': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      'Closed Won': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
      'Closed Lost': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatActivity = (activity: string) => {
    const parsed = topProspectsService.parseActivity(activity);
    return `${parsed.type}: ${parsed.status}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-lg font-semibold">Top Prospects</CardTitle>
        {onRefresh && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Company</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Stage</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Country</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Last Activity</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Team</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="border-b border-border/50">
                    <td className="py-3 px-3">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                    </td>
                    <td className="py-3 px-3"><Skeleton className="h-6 w-20" /></td>
                    <td className="py-3 px-3"><Skeleton className="h-4 w-16" /></td>
                    <td className="py-3 px-3">
                      <div className="space-y-1">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="space-y-1">
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : prospects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    No prospects found
                  </td>
                </tr>
              ) : (
                prospects.map((prospect) => (
                <tr
                  key={prospect.id}
                  className="border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{prospect.name}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <Badge className={cn('text-xs', getStageColor(prospect.current_stage))}>
                      {prospect.current_stage}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 text-sm text-muted-foreground">
                    {prospect.country || 'N/A'}
                  </td>
                  <td className="py-3 px-3">
                    <div className="text-xs">
                      <div className="text-foreground">{formatActivity(prospect.last_activity)}</div>
                      <div className="text-muted-foreground">
                        {formatDate(prospect.last_activity_date || prospect.last_activity_updated_at)}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <div className="text-xs space-y-1">
                      <div className="text-foreground">
                        <span className="font-medium">Assigned:</span> {prospect.assigned_to_name || `User ${prospect.assigned_to}`}
                      </div>
                      <div className="text-muted-foreground">
                        <span className="font-medium">Created:</span> {prospect.created_by_name || `User ${prospect.created_by}`}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TopProspectsTable;
